import { useCallback, useEffect, useRef, useState } from 'react';

interface UseRecordingCountdownReturn {
    countdown: number | null;
    start: () => void;
    cancel: () => void;
}

// Shared 3-2-1 pre-roll countdown, extracted from VoiceIsolator.js and VoiceChanger.js.
export function useRecordingCountdown(onComplete: () => void, countdownSec: number = 3): UseRecordingCountdownReturn {
    const [countdown, setCountdown] = useState<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const clearTimers = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const start = useCallback(() => {
        if (countdown !== null) return;

        if (countdownSec <= 0) {
            onCompleteRef.current();
            return;
        }

        setCountdown(countdownSec);
        intervalRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev === null) return null;
                if (prev <= 1) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    timeoutRef.current = setTimeout(() => {
                        setCountdown(null);
                        onCompleteRef.current();
                    }, 700);
                    return prev;
                }
                return prev - 1;
            });
        }, 1000);
    }, [countdown, countdownSec]);

    const cancel = useCallback(() => {
        clearTimers();
        setCountdown(null);
    }, [clearTimers]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    return { countdown, start, cancel };
}
